import { Mail, Building2, MapPin, Globe, Store } from "lucide-react";
import Seo from "@/components/Seo";
import PageHeader from "@/components/PageHeader";
import AmazonButton from "@/components/AmazonButton";
import { SITE } from "@/lib/site";

export default function Contact() {
  const details = [
    { icon: Building2, label: "Company name", value: SITE.legalName, testId: "contact-company-name" },
    { icon: Building2, label: "Brand name", value: SITE.brand, testId: "contact-brand-name" },
  ];

  return (
    <>
      <Seo
        title="Contact | AutoNeova"
        description="Contact AutoNeova, a U.S.-based automotive e-commerce company. Business inquiries by email; Amazon order questions through your Amazon account."
        path="/contact"
      />
      <PageHeader
        testId="contact-page-header"
        eyebrow="Contact"
        title="Get in touch with AutoNeova"
        intro="We are happy to answer general business inquiries by email. Order-specific questions are handled through Amazon."
      />

      <section className="bg-white py-16 lg:py-24" data-testid="contact-page-content">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 lg:grid-cols-12 lg:px-8">
          <div className="lg:col-span-7">
            <h2 className="text-2xl font-extrabold text-[#0B1F33] md:text-3xl">
              Company Information
            </h2>
            <dl className="mt-8 divide-y divide-[#0B1F33]/10 border-y border-[#0B1F33]/10">
              {details.map((d) => (
                <div key={d.label} className="flex gap-5 py-5">
                  <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[#1F6FEB]/10 text-[#1F6FEB]">
                    <d.icon className="h-[18px] w-[18px]" aria-hidden="true" />
                  </span>
                  <div>
                    <dt className="text-xs font-semibold uppercase tracking-wider text-[#263238]/60">
                      {d.label}
                    </dt>
                    <dd data-testid={d.testId} className="mt-1 text-base font-medium text-[#0B1F33]">
                      {d.value}
                    </dd>
                  </div>
                </div>
              ))}

              <div className="flex gap-5 py-5">
                <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[#1F6FEB]/10 text-[#1F6FEB]">
                  <Building2 className="h-[18px] w-[18px]" aria-hidden="true" />
                </span>
                <div>
                  <dt className="text-xs font-semibold uppercase tracking-wider text-[#263238]/60">
                    Registered Business Address
                  </dt>
                  <dd
                    data-testid="contact-registered-address"
                    className="mt-1 text-base font-medium not-italic text-[#0B1F33]"
                  >
                    <address className="not-italic">
                      {SITE.address.line1}
                      <br />
                      {`${SITE.address.city}, ${SITE.address.state} ${SITE.address.postalCode}`}
                    </address>
                  </dd>
                </div>
              </div>

              <div className="flex gap-5 py-5">
                <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[#1F6FEB]/10 text-[#1F6FEB]">
                  <MapPin className="h-[18px] w-[18px]" aria-hidden="true" />
                </span>
                <div>
                  <dt className="text-xs font-semibold uppercase tracking-wider text-[#263238]/60">
                    Business location
                  </dt>
                  <dd data-testid="contact-location" className="mt-1 text-base font-medium text-[#0B1F33]">
                    {SITE.location}
                  </dd>
                </div>
              </div>

              <div className="flex gap-5 py-5">
                <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[#1F6FEB]/10 text-[#1F6FEB]">
                  <Mail className="h-[18px] w-[18px]" aria-hidden="true" />
                </span>
                <div>
                  <dt className="text-xs font-semibold uppercase tracking-wider text-[#263238]/60">
                    Business email
                  </dt>
                  <dd className="mt-1">
                    <a
                      href={`mailto:${SITE.email}`}
                      data-testid="contact-email-link"
                      className="text-base font-semibold text-[#1F6FEB] underline decoration-[#1F6FEB]/30 underline-offset-4 transition-colors hover:decoration-[#1F6FEB]"
                    >
                      {SITE.email}
                    </a>
                  </dd>
                </div>
              </div>

              <div className="flex gap-5 py-5">
                <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[#1F6FEB]/10 text-[#1F6FEB]">
                  <Globe className="h-[18px] w-[18px]" aria-hidden="true" />
                </span>
                <div>
                  <dt className="text-xs font-semibold uppercase tracking-wider text-[#263238]/60">
                    Website
                  </dt>
                  <dd data-testid="contact-website" className="mt-1 text-base font-medium text-[#0B1F33]">
                    {SITE.website}
                  </dd>
                </div>
              </div>

              <div className="flex gap-5 py-5">
                <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[#1F6FEB]/10 text-[#1F6FEB]">
                  <Store className="h-[18px] w-[18px]" aria-hidden="true" />
                </span>
                <div>
                  <dt className="text-xs font-semibold uppercase tracking-wider text-[#263238]/60">
                    Amazon storefront
                  </dt>
                  <dd className="mt-1">
                    <a
                      href={SITE.amazonStore}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="contact-amazon-link"
                      className="break-all text-base font-semibold text-[#1F6FEB] underline decoration-[#1F6FEB]/30 underline-offset-4 transition-colors hover:decoration-[#1F6FEB]"
                    >
                      {SITE.amazonStore}
                    </a>
                  </dd>
                </div>
              </div>
            </dl>

            <p className="mt-6 text-sm text-[#263238]/70">
              Email is our primary channel for business correspondence. The address above is our
              registered business address for legal and administrative correspondence only.
            </p>
          </div>

          <aside className="lg:col-span-5">
            <div className="rounded-lg border border-[#0B1F33]/10 bg-[#F4F6F8] p-8">
              <h2 className="text-xl font-bold text-[#0B1F33]">How to reach the right place</h2>
              <p className="mt-4 text-base leading-relaxed text-[#263238]">
                For questions concerning an Amazon order, customers should use the order-support
                options available within their Amazon account. For general business inquiries, please
                contact AutoNeova by email.
              </p>
              <a
                href={`mailto:${SITE.email}`}
                data-testid="contact-email-button"
                className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#1F6FEB] px-6 py-3 text-sm font-semibold text-white transition-[background-color,transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:bg-[#1655B8]"
              >
                <Mail className="h-4 w-4" aria-hidden="true" />
                Email AutoNeova
              </a>
              <AmazonButton testId="contact-amazon-cta" variant="outline" className="mt-4 w-full" />
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}
